import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { User as UserEntity, FlightBooking, Passenger, AbandonedTrip } from '@/api/entities';
import { ArrowLeft, Plane, Users, ShoppingCart, Calendar, Loader2, AlertCircle, Edit, Save, X, UserCheck, Sparkles, Plus, Minus, Wallet, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { createPageUrl, lifetimeMilesTotals } from '@/utils';
import { format, parseISO } from 'date-fns';
import { getCityName } from '@/components/airportUtils';
import { toast } from 'sonner';
import SectionBand from '@/components/ui/SectionBand';
import FlightDetailsModal from '@/components/flight/FlightDetailsModal';
import { getSeatPreferenceLabel } from '@/utils/seatPreference';
import { referralSourceLabel } from '@/utils/referralSource';
import { deferaCreditUrl } from '@/lib/apiUrls';
import { mergeSupabaseEdgeHeaders } from '@/lib/reserveActAs';
import { CUSTOMER_NOTE_MAX_LENGTH } from '../../supabase/functions/_shared/defera-credit-history.ts';
import { cn } from '@/lib/utils';
import RoyalAmbientBackground from '@/components/ui/RoyalAmbientBackground';
import { REWARDS_SECTION_CARD, BRAND_PRIMARY_BTN_CLASS } from '@/constants/brandColors';
import { fmtAmount, fmtCurrency } from '@/utils/money';
import { bookingTravelAmount, customerTravelSpent } from '@/utils/travelTotals';
import VipBadge from '@/components/admin/VipBadge';

const formatUsdFromCents = (cents) =>
  `$${(Math.round(Number(cents) || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Same input chrome as Profile / checkout PassengerForm so admin and customer
// account surfaces read as one product.
const FIELD_BASE =
  'bg-white/[0.03] border-white/10 text-white placeholder:text-white/35 h-10 rounded-xl text-base md:text-sm font-normal px-3.5 transition-colors hover:bg-white/[0.05] focus-visible:ring-1 focus-visible:ring-[hsl(228,35%,58%)]/30 focus-visible:ring-offset-0 focus-visible:border-[hsl(228,35%,58%)]/30';

/** Royal-tinted interactive list row (bookings, passengers, carts). */
const ROW_INTERACTIVE =
  'bg-brand-900/20 border border-brand-800/40 rounded-2xl p-4 transition-all duration-300 hover:bg-brand-900/35 hover:border-brand-700/45';

function HeroChip({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-800/45 bg-brand-900/30 px-3 py-1 text-[11px] font-normal text-white/65">
      <Icon className="w-3 h-3 text-[hsl(228,35%,58%)]" />
      {children}
    </span>
  );
}

export default function UserDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(null);
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [passengers, setPassengers] = useState([]);
  const [savedPassengers, setSavedPassengers] = useState([]);
  const [abandonedTrips, setAbandonedTrips] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [editedFirstName, setEditedFirstName] = useState('');
  const [editedLastName, setEditedLastName] = useState('');
  const [editedEmail, setEditedEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTripGroup, setSelectedTripGroup] = useState(null);
  const [showFlightModal, setShowFlightModal] = useState(false);
  const [selectedCart, setSelectedCart] = useState(null);
  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const [editedPassengerData, setEditedPassengerData] = useState({
    givenName: '',
    familyName: '',
    bornOn: ''
  });
  const [stats, setStats] = useState({
    totalBookings: 0,
    totalSpent: 0,
    totalPassengers: 0,
    abandonedCarts: 0,
    confirmedTrips: 0,
    pendingTrips: 0
  });
  const [milesBalance, setMilesBalance] = useState(0);
  const [milesEarnedTotal, setMilesEarnedTotal] = useState(0);
  const [milesRedeemedTotal, setMilesRedeemedTotal] = useState(0);
  const [milesLedger, setMilesLedger] = useState([]);
  const [showRedemptionModal, setShowRedemptionModal] = useState(false);
  const [redemptionAmount, setRedemptionAmount] = useState('');
  const [redemptionReason, setRedemptionReason] = useState('');
  const [showInjectModal, setShowInjectModal] = useState(false);
  const [injectAmount, setInjectAmount] = useState('');
  const [injectReason, setInjectReason] = useState('');
  // Defera Credit (house credit) — cash-value ledger, distinct from Defera Miles.
  const [deferaCreditCents, setDeferaCreditCents] = useState(0);
  const [deferaCreditLedger, setDeferaCreditLedger] = useState([]);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditMode, setCreditMode] = useState('credit'); // 'credit' | 'adjustment'
  const [creditAmountInput, setCreditAmountInput] = useState('');
  // Internal: stored as the ledger description, never shown to the customer.
  const [creditReason, setCreditReason] = useState('');
  // Optional line the customer sees in their Credit history (customer_note).
  const [creditCustomerNote, setCreditCustomerNote] = useState('');
  // Stable per modal session so a slow-network resubmit dedupes on the server's
  // idempotency guard instead of double-granting. Regenerated each time the
  // modal is opened.
  const [creditIdemKey, setCreditIdemKey] = useState('');

  const openCreditModal = (mode) => {
    setCreditMode(mode);
    setCreditIdemKey(
      (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    );
    setShowCreditModal(true);
  };

  const loadUserData = useCallback(async () => {
    try {
      const me = await UserEntity.me();
      setCurrentUser(me);

      if (me.role !== 'admin') {
        setIsLoading(false);
        return;
      }

      const params = new URLSearchParams(location.search);
      const userId = params.get('id');

      if (!userId) {
        navigate(createPageUrl('Users'));
        return;
      }

      const userData = await UserEntity.findById(userId);
      setUser(userData);
      setEditedFirstName(userData.firstName || '');
      setEditedLastName(userData.lastName || '');
      setEditedEmail(userData.email || '');

      // Get session from Supabase (import once and reuse)
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No session found');
      }

      // Fetch user details via backend admin endpoint (bypasses RLS)
      const response = await fetch(`/api/admin/user-details/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'omit'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch user details: ${response.status}`);
      }

      const { bookings: userBookings, passengers: userPassengers, savedPassengers: userSavedPassengers, abandonedTrips: userAbandoned } = await response.json();

      // Sort by most recent
      const sortedBookings = userBookings
        .map(b => new FlightBooking(b))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      const sortedPassengers = userPassengers
        .map(p => new Passenger(p))
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

      const sortedSavedPassengers = (userSavedPassengers || []).map(p => ({
        id: p.id,
        title: p.title || '',
        first_name: p.given_name || p.first_name || '',
        middle_name: p.middle_name || '',
        last_name: p.family_name || p.last_name || '',
        gender: p.gender || '',
        email: p.email || '',
        phone: p.phone_number || p.phone || '',
        date_of_birth: p.born_on || p.date_of_birth || '',
        passport_number: p.passport_number || '',
        passport_expiry: p.passport_expiry_date || p.passport_expiry || '',
        passport_country: p.passport_country || '',
        nationality: p.nationality || '',
        frequent_flyer_program: p.frequent_flyer_program || '',
        frequent_flyer_number: p.frequent_flyer_number || '',
        known_traveler_number: p.known_traveler_number || '',
        special_requests: p.special_requests || '',
        seat_preference: p.seat_preference || '',
        created_at: p.created_at,
        updated_at: p.updated_at
      }));

      const sortedAbandoned = userAbandoned
        .map(a => new AbandonedTrip(a))
        .sort((a, b) => new Date(b.created_at || b.last_activity || 0) - new Date(a.created_at || a.last_activity || 0));

      setBookings(sortedBookings);
      setPassengers(sortedPassengers);
      setSavedPassengers(sortedSavedPassengers);
      setAbandonedTrips(sortedAbandoned);

      // Spent on trips: a Faye travel insurance premium is collected for Faye.
      const totalSpent = customerTravelSpent(userBookings);
      const confirmedTrips = userBookings.filter(b => b.trip_status === 'confirmed' || b.status === 'confirmed').length;
      const pendingTrips = userBookings.filter(b => b.trip_status === 'pending' || b.status === 'pending').length;

      setStats({
        totalBookings: userBookings.length,
        totalSpent: totalSpent,
        totalPassengers: new Set(userPassengers.map(p => `${p.givenName || p.firstName} ${p.familyName || p.lastName}`)).size,
        abandonedCarts: userAbandoned.filter(a => a.status === 'in_cart').length,
        confirmedTrips: confirmedTrips,
        pendingTrips: pendingTrips
      });

      // Fetch miles data from ledger
      try {
        const balanceResponse = await fetch(`/api/miles/balance/${userId}`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });

        if (balanceResponse.ok) {
          const { balance } = await balanceResponse.json();
          setMilesBalance(balance || 0);
        } else {
          setMilesBalance(0);
        }

        const ledgerResponse = await fetch(`/api/miles/ledger/${userId}`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });

        if (ledgerResponse.ok) {
          const { transactions } = await ledgerResponse.json();
          setMilesLedger(transactions || []);

          const totals = lifetimeMilesTotals(transactions);
          setMilesEarnedTotal(totals.earned);
          setMilesRedeemedTotal(totals.redeemed);
        } else {
          setMilesLedger([]);
          setMilesEarnedTotal(0);
          setMilesRedeemedTotal(0);
        }
      } catch (milesError) {
        console.error('Error fetching miles data:', milesError);
        setMilesBalance(0);
        setMilesLedger([]);
        setMilesEarnedTotal(0);
        setMilesRedeemedTotal(0);
      }

      // Defera Credit (house credit) balance + ledger.
      try {
        const creditResp = await fetch(deferaCreditUrl(`admin?user_id=${userId}`), {
          headers: mergeSupabaseEdgeHeaders({ 'Authorization': `Bearer ${session.access_token}` }),
          credentials: 'omit',
        });
        if (creditResp.ok) {
          const creditData = await creditResp.json();
          setDeferaCreditCents(creditData.balance_cents || 0);
          setDeferaCreditLedger(creditData.ledger || []);
        } else {
          setDeferaCreditCents(0);
          setDeferaCreditLedger([]);
        }
      } catch (creditError) {
        console.error('Error fetching Defera Credit data:', creditError);
        setDeferaCreditCents(0);
        setDeferaCreditLedger([]);
      }
    } catch (error) {
      console.error('Error loading user details:', error);
      toast.error('Failed to load user details');
    } finally {
      setIsLoading(false);
    }
  }, [location.search, navigate]);

  const refreshDeferaCredit = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const resp = await fetch(deferaCreditUrl(`admin?user_id=${user.id}`), {
        headers: mergeSupabaseEdgeHeaders({ 'Authorization': `Bearer ${session.access_token}` }),
        credentials: 'omit',
      });
      if (resp.ok) {
        const data = await resp.json();
        setDeferaCreditCents(data.balance_cents || 0);
        setDeferaCreditLedger(data.ledger || []);
      }
    } catch (e) {
      console.error('Error refreshing Defera Credit:', e);
    }
  }, [user?.id]);

  const handleGrantCredit = async () => {
    setIsSaving(true);
    try {
      const dollars = parseFloat(creditAmountInput);
      if (!Number.isFinite(dollars) || dollars === 0) {
        toast.error('Please enter a non-zero dollar amount');
        setIsSaving(false);
        return;
      }
      if (creditMode === 'credit' && dollars < 0) {
        toast.error('Use Adjust for negative amounts');
        setIsSaving(false);
        return;
      }
      if (!creditReason || creditReason.trim().length < 10) {
        toast.error('Please provide a reason (minimum 10 characters)');
        setIsSaving(false);
        return;
      }
      const customerNote = creditCustomerNote.trim();
      if (customerNote.length > CUSTOMER_NOTE_MAX_LENGTH) {
        toast.error(`The note for the customer must be ${CUSTOMER_NOTE_MAX_LENGTH} characters or fewer`);
        setIsSaving(false);
        return;
      }
      const amountCents = Math.round(dollars * 100);

      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session found');

      const resp = await fetch(deferaCreditUrl('admin'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        }),
        credentials: 'omit',
        body: JSON.stringify({
          user_id: user.id,
          amount_cents: amountCents,
          description: creditReason.trim(),
          ...(customerNote ? { customer_note: customerNote } : {}),
          type: creditMode,
          idempotency_key: creditIdemKey || `${user.id}-${Date.now()}`,
        }),
      });

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.error || `Failed (${resp.status})`);
      }
      const result = await resp.json().catch(() => ({}));

      await refreshDeferaCredit();
      setShowCreditModal(false);
      setCreditAmountInput('');
      setCreditReason('');
      setCreditCustomerNote('');
      toast.success(
        creditMode === 'credit'
          ? `Granted ${formatUsdFromCents(amountCents)} in Defera Credit`
          : `Adjusted Defera Credit by ${formatUsdFromCents(amountCents)}`,
      );
      // The grant stands either way; only the customer's line is missing.
      if (customerNote && result?.customer_note_saved !== true) {
        toast.warning('The note for the customer wasn’t saved. They’ll see the standard label for this entry.');
      }
    } catch (error) {
      console.error('Error granting Defera Credit:', error);
      toast.error(error.message || 'Failed to update Defera Credit');
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const handleSaveUser = async () => {
    setIsSaving(true);
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No session found');
      }

      // Update user data via backend admin endpoint
      const response = await fetch(`/api/admin/update-user/${user.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'omit',
        body: JSON.stringify({
          firstName: editedFirstName,
          lastName: editedLastName,
          email: editedEmail
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update user');
      }

      const updatedUser = await response.json();
      setUser({
        ...user,
        firstName: updatedUser.firstName,
        lastName: updatedUser.lastName,
        email: updatedUser.email
      });
      setIsEditingUser(false);
      toast.success('User updated successfully');
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.message || 'Failed to update user');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Click handler for a Booking History row. Opens the same FlightDetailsModal
   * the customer Dashboard uses, so the admin sees the full "trip dashboard"
   * (cost breakdown, payment schedule, cancel/edit, comments, etc.) in-place
   * rather than the legacy minimal Dialog.
   *
   * Group all bookings that share a trip_reference (multi-leg trips,
   * round-trips with separate FlightBooking rows, etc.) so the modal can
   * render every leg under one trip — matches Dashboard's grouping logic
   * exactly. Falls back to booking_reference / itinerary_image_url / id when
   * trip_reference is null (legacy rows or hold-only bookings).
   */
  const handleOpenBookingModal = (booking) => {
    const groupKey =
      booking.trip_reference ||
      booking.tripReference ||
      booking.booking_reference ||
      booking.bookingReference ||
      booking.itinerary_image_url ||
      booking.id;

    const tripFlights = bookings.filter((b) => {
      const k =
        b.trip_reference ||
        b.tripReference ||
        b.booking_reference ||
        b.bookingReference ||
        b.itinerary_image_url ||
        b.id;
      return k === groupKey;
    });

    setSelectedTripGroup(tripFlights.length > 0 ? tripFlights : [booking]);
    setShowFlightModal(true);
  };

  const handleEditPassenger = (passenger) => {
    setSelectedPassenger(passenger);
    setEditedPassengerData({
      givenName: passenger.givenName || passenger.firstName || '',
      familyName: passenger.familyName || passenger.lastName || '',
      bornOn: passenger.bornOn || ''
    });
  };

  const handleSavePassenger = async () => {
    setIsSaving(true);
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No session found');
      }

      // Update passenger data via Supabase directly
      const { error } = await supabase
        .from('passengers')
        .update({
          given_name: editedPassengerData.givenName,
          family_name: editedPassengerData.familyName,
          born_on: editedPassengerData.bornOn
        })
        .eq('id', selectedPassenger.id);

      if (error) {
        throw new Error(error.message);
      }

      // Update local state
      const updatedPassengers = passengers.map(p => 
        p.id === selectedPassenger.id 
          ? new Passenger({
              ...p,
              given_name: editedPassengerData.givenName,
              family_name: editedPassengerData.familyName,
              born_on: editedPassengerData.bornOn
            })
          : p
      );
      setPassengers(updatedPassengers);
      setSelectedPassenger(null);
      toast.success('Passenger updated successfully');
    } catch (error) {
      console.error('Error updating passenger:', error);
      toast.error(error.message || 'Failed to update passenger');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRedeemMiles = async () => {
    setIsSaving(true);
    try {
      const amount = parseInt(redemptionAmount);
      
      // Validation
      if (!amount || amount <= 0) {
        toast.error('Please enter a valid positive amount');
        setIsSaving(false);
        return;
      }
      
      if (amount > milesBalance) {
        toast.error(`Low balance. Available: ${milesBalance.toLocaleString()} miles`);
        setIsSaving(false);
        return;
      }
      
      if (!redemptionReason || redemptionReason.trim().length < 10) {
        toast.error('Please provide a reason (minimum 10 characters)');
        setIsSaving(false);
        return;
      }
      
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No session found');
      }

      // Call admin redemption endpoint
      const response = await fetch(`/api/miles/admin-redemption`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          targetUserId: user.id,
          amount: amount,
          reason: redemptionReason.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to redeem miles');
      }

      const result = await response.json();
      
      // Refresh miles data after redemption
      const balanceResponse = await fetch(`/api/miles/balance/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      
      if (balanceResponse.ok) {
        const { balance } = await balanceResponse.json();
        setMilesBalance(balance || 0);
      }
      
      // Refresh ledger
      const ledgerResponse = await fetch(`/api/miles/ledger/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      
      if (ledgerResponse.ok) {
        const { transactions } = await ledgerResponse.json();
        setMilesLedger(transactions || []);

        // Recalculate totals (same rules as the first load)
        const totals = lifetimeMilesTotals(transactions);
        setMilesEarnedTotal(totals.earned);
        setMilesRedeemedTotal(totals.redeemed);
      }
      
      // Close modal and reset form
      setShowRedemptionModal(false);
      setRedemptionAmount('');
      setRedemptionReason('');
      toast.success(`Successfully redeemed ${amount.toLocaleString()} miles`);
    } catch (error) {
      console.error('Error redeeming miles:', error);
      toast.error(error.message || 'Failed to redeem miles');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInjectMiles = async () => {
    setIsSaving(true);
    try {
      const amount = parseInt(injectAmount);
      if (!amount || amount <= 0) {
        toast.error('Please enter a valid positive amount');
        setIsSaving(false);
        return;
      }
      if (!injectReason || injectReason.trim().length < 10) {
        toast.error('Please provide a reason (minimum 10 characters)');
        setIsSaving(false);
        return;
      }

      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session found');

      const response = await fetch('/api/admin/miles/inject', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          targetUserId: user.id,
          amount,
          reason: injectReason.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to inject miles');
      }

      const result = await response.json();
      setMilesBalance(result.balance || 0);

      const ledgerResponse = await fetch(`/api/miles/ledger/${user.id}`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (ledgerResponse.ok) {
        const { transactions } = await ledgerResponse.json();
        setMilesLedger(transactions || []);
        setMilesEarnedTotal(lifetimeMilesTotals(transactions).earned);
      }

      setShowInjectModal(false);
      setInjectAmount('');
      setInjectReason('');
      toast.success(`Successfully injected ${amount.toLocaleString()} miles`);
    } catch (error) {
      console.error('Error injecting miles:', error);
      toast.error(error.message || 'Failed to inject miles');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[70vh] relative flex items-center justify-center">
        <RoyalAmbientBackground />
        <div className="relative z-10 flex items-center gap-2.5 text-white/50 font-normal">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading user…
        </div>
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-[70vh] relative flex items-center justify-center text-center p-4">
        <RoyalAmbientBackground />
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className={cn(REWARDS_SECTION_CARD, 'relative z-10 p-8')}>
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-light text-white mb-2 tracking-tight">Access Denied</h2>
          <p className="text-white/60 text-sm font-normal">Administrator access required.</p>
        </motion.div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-[70vh] relative flex items-center justify-center">
        <RoyalAmbientBackground />
        <p className="relative z-10 text-white/60 font-normal">User not found</p>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    if (!status) return null;
    const styles = {
      pending: 'bg-brand-500/10 text-brand-400 border-brand-500/20',
      confirmed: 'bg-green-500/10 text-green-400 border-green-500/20',
      completed: 'bg-sky-400/10 text-sky-300 border-sky-400/20',
      cancelled: 'bg-red-500/10 text-red-400 border-red-500/20',
      defaulted: 'bg-orange-600/20 text-orange-400 border-orange-600/30',
      delinquent: 'bg-amber-500/10 text-amber-300 border-amber-500/25',
      in_cart: 'bg-brand-500/10 text-brand-400 border-brand-500/20',
      active: 'bg-brand-500/10 text-brand-400 border-brand-500/20'
    };
    return <Badge className={`text-xs px-2 py-1 font-normal ${styles[status] || styles.pending}`}>{status.replace('_', ' ')}</Badge>;
  };

  const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ');
  const initials = ((user.firstName?.[0] || '') + (user.lastName?.[0] || '')).toUpperCase()
    || (user.email?.[0] || '?').toUpperCase();
  const displayRole = user.custom_role || (user.role === 'admin' ? 'Admin' : 'User');

  return (
    <div className="min-h-[70vh] py-8 relative">
      <RoyalAmbientBackground />
      <div className="max-w-6xl mx-auto px-6 md:px-8 relative z-10">
        {/* Masthead */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-start gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(createPageUrl('Users'))} className="text-white/60 hover:text-white hover:bg-white/10 h-10 w-10 rounded-full flex-shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(228,35%,58%)]/85 font-medium">
                Admin · Users
              </p>
              <h1 className="text-2xl md:text-[32px] font-light text-white tracking-tight leading-none">
                User Details
              </h1>
              <p className="text-white/60 text-[13px] font-normal leading-snug">
                Complete history, balances, and travelers for this account.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Identity hero — same visual grammar as the customer Profile hero so
            admin and customer account surfaces read as one product. Editing
            (pencil) swaps the identity block for the name/email inputs. */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={cn(REWARDS_SECTION_CARD, 'mb-6')}>
          <div className="flex flex-col sm:flex-row sm:items-start gap-5 px-5 sm:px-7 py-6 sm:py-7">
            <div className="w-16 h-16 rounded-full bg-gradient-to-b from-[hsl(228,55%,26%)] to-[hsl(228,67%,12%)] border border-[hsl(228,40%,34%)]/50 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12)] flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xl font-light tracking-wide">{initials}</span>
            </div>
            <div className="min-w-0 flex-1">
              {isEditingUser ? (
                <div className="space-y-3 max-w-md">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input
                      value={editedFirstName}
                      onChange={(e) => setEditedFirstName(e.target.value)}
                      placeholder="First name"
                      className={FIELD_BASE}
                    />
                    <Input
                      value={editedLastName}
                      onChange={(e) => setEditedLastName(e.target.value)}
                      placeholder="Last name"
                      className={FIELD_BASE}
                    />
                  </div>
                  <Input
                    value={editedEmail}
                    onChange={(e) => setEditedEmail(e.target.value)}
                    placeholder="Email"
                    className={FIELD_BASE}
                  />
                  <Button
                    onClick={handleSaveUser}
                    disabled={isSaving}
                    className={cn('rounded-full h-10 px-6 text-[13px]', BRAND_PRIMARY_BTN_CLASS)}
                  >
                    {isSaving ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                    ) : (
                      <><Save className="w-4 h-4 mr-2" /> Save Changes</>
                    )}
                  </Button>
                </div>
              ) : (
                <>
                  <h3 className="text-white text-xl font-light tracking-tight truncate">
                    {displayName || 'Unknown User'}
                  </h3>
                  <p className="text-white/45 text-[13px] font-normal truncate">{user.email}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <HeroChip icon={Shield}>{displayRole}</HeroChip>
                    <VipBadge userId={user.id} />
                    {user.createdAt && (
                      <HeroChip icon={Calendar}>Joined {format(parseISO(user.createdAt), 'MMM d, yyyy')}</HeroChip>
                    )}
                    {user.referral_source && (
                      <HeroChip icon={Sparkles}>
                        {referralSourceLabel(user.referral_source)}
                      </HeroChip>
                    )}
                  </div>
                </>
              )}
            </div>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => {
                if (isEditingUser) {
                  setEditedFirstName(user.firstName || '');
                  setEditedLastName(user.lastName || '');
                  setEditedEmail(user.email || '');
                }
                setIsEditingUser(!isEditingUser);
              }}
              className="text-white/45 hover:text-white hover:bg-white/[0.08] h-8 w-8 rounded-full flex-shrink-0"
              title={isEditingUser ? 'Cancel editing' : 'Edit user'}
            >
              {isEditingUser ? <X className="w-3.5 h-3.5" /> : <Edit className="w-3.5 h-3.5" />}
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-5 sm:px-7 pb-6 pt-5 border-t border-white/[0.06]">
            <div className="bg-brand-900/20 border border-brand-800/40 rounded-2xl p-3.5 text-center">
              <div className="text-2xl font-light text-white tabular-nums">{stats.totalBookings.toLocaleString()}</div>
              <div className="text-[10px] text-white/40 font-normal uppercase tracking-widest mt-1">Bookings</div>
            </div>
            <div className="bg-brand-900/20 border border-brand-800/40 rounded-2xl p-3.5 text-center">
              <div className="text-2xl font-light text-white tabular-nums">${stats.totalSpent.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
              <div className="text-[10px] text-white/40 font-normal uppercase tracking-widest mt-1">Total Spent</div>
            </div>
            <div className="bg-brand-900/20 border border-brand-800/40 rounded-2xl p-3.5 text-center">
              <div className="text-2xl font-light text-white tabular-nums">{stats.totalPassengers.toLocaleString()}</div>
              <div className="text-[10px] text-white/40 font-normal uppercase tracking-widest mt-1">Passengers</div>
            </div>
            <div className="bg-brand-900/20 border border-brand-800/40 rounded-2xl p-3.5 text-center">
              <div className="text-2xl font-light text-white tabular-nums">{stats.abandonedCarts.toLocaleString()}</div>
              <div className="text-[10px] text-white/40 font-normal uppercase tracking-widest mt-1">Abandoned</div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: balances (Defera Miles + Defera Credit), each with its
              own SectionBand — identity lives in the hero above. */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="lg:col-span-1 space-y-6">
            {/* Defera Miles */}
            <div className={REWARDS_SECTION_CARD}>
              <SectionBand
                icon={Sparkles}
                iconColor="text-brand-300"
                accentRgb="10, 18, 51"
                title="Defera Miles"
                rightSlot={
                  /* Icon-only pills — labeled pills truncated the band title at
                     the 1-col width ("DEFERA M…"); color + tooltip carry intent. */
                  <div className="flex gap-1.5">
                    <Button
                      size="icon"
                      onClick={() => setShowInjectModal(true)}
                      title="Inject miles"
                      aria-label="Inject miles"
                      className="h-7 w-7 rounded-full bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 border border-emerald-500/30"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      onClick={() => setShowRedemptionModal(true)}
                      disabled={milesBalance === 0}
                      title="Redeem miles"
                      aria-label="Redeem miles"
                      className="h-7 w-7 rounded-full bg-rose-500/15 hover:bg-rose-500/25 text-rose-200 border border-rose-500/30 disabled:opacity-40"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                }
              />
              <div className="p-6 space-y-3">
                <div className="bg-brand-400/10 border border-brand-400/20 rounded-2xl p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-brand-200/70 font-normal uppercase tracking-widest">Current Balance</span>
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-brand-300" />
                      <span className="text-lg font-light text-brand-100 tabular-nums">{milesBalance.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-brand-900/20 border border-brand-800/40 rounded-xl p-2.5">
                    <div className="text-[10px] text-white/40 font-normal uppercase tracking-widest">Total Earned</div>
                    <div className="text-sm font-normal text-emerald-300 mt-1 tabular-nums">+{milesEarnedTotal.toLocaleString()}</div>
                  </div>
                  <div className="bg-brand-900/20 border border-brand-800/40 rounded-xl p-2.5">
                    <div className="text-[10px] text-white/40 font-normal uppercase tracking-widest">Total Redeemed</div>
                    <div className="text-sm font-normal text-rose-300 mt-1 tabular-nums">-{milesRedeemedTotal.toLocaleString()}</div>
                  </div>
                </div>

                {milesLedger.length > 0 && (
                  <div className="pt-1">
                    <div className="text-[10px] text-white/40 font-normal uppercase tracking-widest mb-2">Recent Transactions</div>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {milesLedger.slice(0, 10).map((transaction, index) => {
                        // Signed: Miles given back (redemption_refund) are Miles in, like an earn.
                        const isEarn = parseFloat(transaction.amount) > 0;
                        return (
                          <div key={transaction.id || index} className="bg-brand-900/20 border border-brand-800/35 rounded-lg p-2 text-xs">
                            <div className="flex items-center justify-between">
                              <span className={`tabular-nums ${isEarn ? 'text-emerald-300' : 'text-rose-300'}`}>
                                {isEarn ? '+' : ''}{parseFloat(transaction.amount).toLocaleString()}
                              </span>
                              <span className="text-white/40 font-normal">
                                {new Date(transaction.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="text-white/60 font-normal mt-0.5 truncate">{transaction.reason}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Defera Credit (house credit — cash value, applied to bookings) */}
            <div className={REWARDS_SECTION_CARD}>
              <SectionBand
                icon={Wallet}
                iconColor="text-emerald-300"
                accentRgb="6, 40, 30"
                title="Defera Credit"
                rightSlot={
                  <div className="flex gap-1.5">
                    <Button
                      size="icon"
                      onClick={() => openCreditModal('credit')}
                      title="Grant credit"
                      aria-label="Grant credit"
                      className="h-7 w-7 rounded-full bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 border border-emerald-500/30"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      onClick={() => openCreditModal('adjustment')}
                      title="Adjust credit"
                      aria-label="Adjust credit"
                      className="h-7 w-7 rounded-full bg-amber-500/15 hover:bg-amber-500/25 text-amber-200 border border-amber-500/30"
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                }
              />
              <div className="p-6 space-y-3">
                <div className="bg-emerald-400/10 border border-emerald-400/20 rounded-2xl p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-emerald-200/70 font-normal uppercase tracking-widest">Available Credit</span>
                    <div className="flex items-center gap-1.5">
                      <Wallet className="w-3.5 h-3.5 text-emerald-300" />
                      <span className="text-lg font-light text-emerald-100 tabular-nums">{formatUsdFromCents(deferaCreditCents)}</span>
                    </div>
                  </div>
                </div>
                <p className="text-[12.5px] text-white/60 font-normal leading-relaxed">
                  Auto-applies at checkout — deposit first, then installments front-to-back, until exhausted. Never expires.
                </p>

                {deferaCreditLedger.length > 0 && (
                  <div className="pt-1">
                    <div className="text-[10px] text-white/40 font-normal uppercase tracking-widest mb-2">Recent Activity</div>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {deferaCreditLedger.slice(0, 10).map((entry, index) => {
                        const cents = Number(entry.amount_cents) || 0;
                        const isPositive = ['credit', 'release', 'adjustment'].includes(entry.type) && cents >= 0;
                        const isDebit = entry.type === 'debit';
                        const sign = isDebit ? '-' : (isPositive ? '+' : '');
                        return (
                          <div key={entry.id || index} className="bg-brand-900/20 border border-brand-800/35 rounded-lg p-2 text-xs">
                            <div className="flex items-center justify-between">
                              <span className={`tabular-nums ${isDebit ? 'text-rose-300' : 'text-emerald-300'}`}>
                                {sign}{formatUsdFromCents(Math.abs(cents))}
                              </span>
                              <span className="text-white/40 font-normal">
                                {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : ''}
                              </span>
                            </div>
                            <div className="text-white/60 font-normal mt-0.5 truncate">
                              <span className="text-white/30 uppercase tracking-wider mr-1.5">{entry.type}</span>
                              {entry.description}
                            </div>
                            {entry.customer_note && (
                              <div className="text-white/40 font-normal mt-0.5 truncate" title={entry.customer_note}>
                                Customer sees: “{entry.customer_note}”
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Right Column - Lists */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="lg:col-span-2 space-y-6">
            {/* Booking History */}
            <div className={REWARDS_SECTION_CARD}>
              <SectionBand
                icon={Plane}
                iconColor="text-[hsl(228,35%,58%)]"
                accentRgb="10, 18, 51"
                title="Booking History"
                rightSlot={
                  <span className="text-[11px] text-white/45 font-normal tabular-nums px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.08]">
                    {bookings.length}
                  </span>
                }
              />
              <div className="p-6">
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {bookings.length > 0 ? (
                    bookings.map((booking) => (
                      <div
                        key={booking.id}
                        onClick={() => handleOpenBookingModal(booking)}
                        className={cn(ROW_INTERACTIVE, 'flex items-center justify-between gap-3 cursor-pointer')}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-normal text-white truncate">
                            {getCityName(booking.departure_city)} → {getCityName(booking.arrival_city)}
                          </p>
                          <p className="text-xs text-white/60 font-normal mt-1">
                            {booking.departure_date ? format(parseISO(booking.departure_date), 'MMM d, yyyy') : 'N/A'}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 text-right">
                          <div>
                            <span className="text-sm font-normal text-white tabular-nums">
                              ${bookingTravelAmount(booking).amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </span>
                            {(() => {
                              const fd = booking.flight_data || booking.flightData || {};
                              const reserveCashCents = fd._reserve_cash_payment_cents ?? fd.reserve_cash_payment_cents ?? 0;
                              const { insurancePremium } = bookingTravelAmount(booking);
                              if (reserveCashCents > 0) {
                                return (
                                  <p className="text-[10px] text-emerald-400/80 font-normal">Reserve + ${fmtCurrency(reserveCashCents)} card</p>
                                );
                              }
                              if (insurancePremium > 0) {
                                // Collected for Faye, so outside the trip amount and Total Spent.
                                return (
                                  <p className="text-[10px] text-white/45 font-normal">+ ${fmtCurrency(Math.round(insurancePremium * 100))} insurance</p>
                                );
                              }
                              return null;
                            })()}
                          </div>
                          {getStatusBadge(booking.trip_status || booking.status)}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-white/60 text-center py-6 font-normal">No bookings yet</p>
                  )}
                </div>
              </div>
            </div>

            {/* Saved Passengers */}
            <div className={REWARDS_SECTION_CARD}>
              <SectionBand
                icon={UserCheck}
                iconColor="text-brand-300"
                accentRgb="52,211,153"
                title="Saved Passengers"
                rightSlot={
                  <span className="text-[11px] text-white/45 font-normal tabular-nums px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.08]">
                    {savedPassengers.length}
                  </span>
                }
              />
              <div className="p-6">
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {savedPassengers.length > 0 ? (
                    savedPassengers.map((passenger) => (
                      <div
                        key={passenger.id}
                        className={ROW_INTERACTIVE}
                      >
                      <p className="text-sm font-normal text-white">
                        {[passenger.title, passenger.first_name, passenger.middle_name, passenger.last_name].filter(Boolean).join(' ')}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-3 text-xs">
                        {passenger.gender && (
                          <p className="text-white/60"><span className="text-white/40">Gender:</span> {passenger.gender}</p>
                        )}
                        {passenger.email && (
                          <p className="text-white/60 truncate"><span className="text-white/40">Email:</span> {passenger.email}</p>
                        )}
                        {passenger.phone && (
                          <p className="text-white/60"><span className="text-white/40">Phone:</span> {passenger.phone}</p>
                        )}
                        {passenger.date_of_birth && (
                          <p className="text-white/60"><span className="text-white/40">DOB:</span> {format(parseISO(passenger.date_of_birth), 'MMM d, yyyy')}</p>
                        )}
                        {passenger.nationality && (
                          <p className="text-white/60"><span className="text-white/40">Nationality:</span> {passenger.nationality}</p>
                        )}
                        {passenger.passport_number && (
                          <p className="text-white/60"><span className="text-white/40">Passport:</span> {passenger.passport_number}</p>
                        )}
                        {passenger.passport_expiry && (
                          <p className="text-white/60"><span className="text-white/40">Passport expiry:</span> {format(parseISO(passenger.passport_expiry), 'MMM d, yyyy')}</p>
                        )}
                        {passenger.passport_country && (
                          <p className="text-white/60"><span className="text-white/40">Passport country:</span> {passenger.passport_country}</p>
                        )}
                        {passenger.frequent_flyer_program && (
                          <p className="text-white/60"><span className="text-white/40">FF program:</span> {passenger.frequent_flyer_program.replace(/_/g, ' ')}</p>
                        )}
                        {passenger.frequent_flyer_number && (
                          <p className="text-white/60"><span className="text-white/40">FF number:</span> {passenger.frequent_flyer_number}</p>
                        )}
                        {passenger.known_traveler_number && (
                          <p className="text-white/60"><span className="text-white/40">KTN:</span> {passenger.known_traveler_number}</p>
                        )}
                        {(() => {
                          const seatLabel = getSeatPreferenceLabel(passenger.seat_preference);
                          return seatLabel ? (
                            <p className="text-white/60"><span className="text-white/40">Seat preference:</span> {seatLabel}</p>
                          ) : null;
                        })()}
                        {passenger.special_requests && (
                          <p className="text-white/60 sm:col-span-2"><span className="text-white/40">Special requests:</span> {passenger.special_requests}</p>
                        )}
                      </div>
                      <p className="text-xs text-white/40 font-normal mt-2">
                        Saved {passenger.created_at ? format(parseISO(passenger.created_at), 'MMM d, yyyy') : 'N/A'}
                        {passenger.updated_at && passenger.updated_at !== passenger.created_at && (
                          <> · Updated {format(parseISO(passenger.updated_at), 'MMM d, yyyy')}</>
                        )}
                      </p>
                    </div>
                  ))
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-12 h-12 rounded-full bg-[hsl(228,40%,38%)]/[0.16] border border-[hsl(228,35%,58%)]/20 flex items-center justify-center mx-auto mb-4">
                        <UserCheck className="w-5 h-5 text-[hsl(228,30%,72%)]" />
                      </div>
                      <p className="text-sm text-white/60 font-normal mb-2">No saved passengers yet</p>
                      <p className="text-xs text-white/40 font-normal">Ask user to save passenger details from their profile</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Passengers on File */}
            <div className={REWARDS_SECTION_CARD}>
              <SectionBand
                icon={Users}
                iconColor="text-brand-300"
                accentRgb="34,211,238"
                title="Passengers on File"
                rightSlot={
                  <span className="text-[11px] text-white/45 font-normal tabular-nums px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.08]">
                    {passengers.length}
                  </span>
                }
              />
              <div className="p-6">
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {passengers.length > 0 ? (
                    passengers.map((passenger) => (
                      <div
                        key={passenger.id}
                        onClick={() => handleEditPassenger(passenger)}
                        className={cn(ROW_INTERACTIVE, 'cursor-pointer')}
                      >
                      <p className="text-sm font-normal text-white">
                        {[passenger.title, passenger.givenName || passenger.firstName, passenger.middleName || passenger.middle_name, passenger.familyName || passenger.lastName].filter(Boolean).join(' ')}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-3 text-xs">
                        {(passenger.type || 'Adult') && (
                          <p className="text-white/60"><span className="text-white/40">Type:</span> {passenger.type || 'Adult'}</p>
                        )}
                        {passenger.gender && (
                          <p className="text-white/60"><span className="text-white/40">Gender:</span> {passenger.gender}</p>
                        )}
                        {passenger.email && (
                          <p className="text-white/60 truncate"><span className="text-white/40">Email:</span> {passenger.email}</p>
                        )}
                        {(passenger.phoneNumber || passenger.phone) && (
                          <p className="text-white/60"><span className="text-white/40">Phone:</span> {passenger.phoneNumber || passenger.phone}</p>
                        )}
                        {passenger.bornOn && (
                          <p className="text-white/60"><span className="text-white/40">DOB:</span> {format(parseISO(passenger.bornOn), 'MMM d, yyyy')}</p>
                        )}
                        {passenger.nationality && (
                          <p className="text-white/60"><span className="text-white/40">Nationality:</span> {passenger.nationality}</p>
                        )}
                        {passenger.passportNumber && (
                          <p className="text-white/60"><span className="text-white/40">Passport:</span> {passenger.passportNumber}</p>
                        )}
                        {passenger.passportExpiryDate && (
                          <p className="text-white/60"><span className="text-white/40">Passport expiry:</span> {format(parseISO(passenger.passportExpiryDate), 'MMM d, yyyy')}</p>
                        )}
                        {passenger.passportCountry && (
                          <p className="text-white/60"><span className="text-white/40">Passport country:</span> {passenger.passportCountry}</p>
                        )}
                        {passenger.frequentFlyerProgram && (
                          <p className="text-white/60"><span className="text-white/40">FF program:</span> {(passenger.frequentFlyerProgram || '').replace(/_/g, ' ')}</p>
                        )}
                        {passenger.frequentFlyerNumber && (
                          <p className="text-white/60"><span className="text-white/40">FF number:</span> {passenger.frequentFlyerNumber}</p>
                        )}
                        {passenger.knownTravelerNumber && (
                          <p className="text-white/60"><span className="text-white/40">KTN:</span> {passenger.knownTravelerNumber}</p>
                        )}
                        {(() => {
                          const seatLabel = getSeatPreferenceLabel(passenger.seatPreference || passenger.seat_preference);
                          return seatLabel ? (
                            <p className="text-white/60"><span className="text-white/40">Seat preference:</span> {seatLabel}</p>
                          ) : null;
                        })()}
                        {passenger.specialRequests && (
                          <p className="text-white/60 sm:col-span-2"><span className="text-white/40">Special requests:</span> {passenger.specialRequests}</p>
                        )}
                      </div>
                      <p className="text-xs text-white/40 font-normal mt-2">
                        {passenger.createdAt ? format(parseISO(passenger.createdAt), 'MMM d, yyyy') : 'N/A'}
                        {passenger.updatedAt && passenger.updatedAt !== passenger.createdAt && (
                          <> · Updated {format(parseISO(passenger.updatedAt), 'MMM d, yyyy')}</>
                        )}
                      </p>
                    </div>
                  ))
                  ) : (
                    <p className="text-sm text-white/60 text-center py-6 font-normal">No passengers on file</p>
                  )}
                </div>
              </div>
            </div>

            {/* Abandoned Carts */}
            <div className={REWARDS_SECTION_CARD}>
              <SectionBand
                icon={ShoppingCart}
                iconColor="text-brand-300"
                accentRgb="251,113,133"
                title="Abandoned Carts"
                rightSlot={
                  <span className="text-[11px] text-white/45 font-normal tabular-nums px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.08]">
                    {abandonedTrips.length}
                  </span>
                }
              />
              <div className="p-6">
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {abandonedTrips.length > 0 ? (
                    abandonedTrips.map((cart) => (
                      <div
                        key={cart.id}
                        onClick={() => setSelectedCart(cart)}
                        className={cn(ROW_INTERACTIVE, 'flex items-center justify-between gap-3 cursor-pointer')}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-normal text-white truncate">
                            {getCityName(cart.departure_city)} → {getCityName(cart.arrival_city)}
                          </p>
                          <p className="text-xs text-white/60 font-normal mt-1">
                            {cart.step_reached ? cart.step_reached.replace('_', ' ') : 'Started'} • {cart.last_activity ? format(parseISO(cart.last_activity), 'MMM d, yyyy') : 'N/A'}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-normal text-white tabular-nums">
                            ${parseFloat(cart.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </span>
                          {getStatusBadge(cart.status)}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-white/60 text-center py-6 font-normal">No abandoned carts</p>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Trip Dashboard Modal — same component the customer Dashboard uses, so
          admin sees the full trip view (cost breakdown, payment schedule,
          comments, edit/cancel) instead of a stripped-down summary dialog. */}
      <FlightDetailsModal
        trip={selectedTripGroup}
        isOpen={showFlightModal}
        onClose={() => {
          setShowFlightModal(false);
          setSelectedTripGroup(null);
        }}
        onUpdate={loadUserData}
        currentUser={currentUser}
      />

      {/* Abandoned Cart Details Dialog - Dark Theme */}
      <Dialog open={!!selectedCart} onOpenChange={() => setSelectedCart(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-gradient-to-b from-[#0A1233] to-[#070E28] border-brand-800/50 rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-white font-normal tracking-tight">Abandoned Cart Details</DialogTitle>
            <DialogDescription className="text-white/60 font-normal">Incomplete booking information</DialogDescription>
          </DialogHeader>
          {selectedCart && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-normal text-white">{getCityName(selectedCart.departure_city)} → {getCityName(selectedCart.arrival_city)}</p>
                  <p className="text-sm text-white/60 font-normal">
                    Last Activity: {selectedCart.last_activity ? format(parseISO(selectedCart.last_activity), 'MMMM d, yyyy h:mm a') : 'N/A'}
                  </p>
                </div>
                {getStatusBadge(selectedCart.status)}
              </div>
              <div className="grid grid-cols-2 gap-4 p-4 bg-white/5 rounded-2xl border border-white/10">
                <div>
                  <p className="text-sm text-white/60 font-normal">Step Reached</p>
                  <p className="font-normal text-white capitalize">{selectedCart.step_reached ? selectedCart.step_reached.replace('_', ' ') : 'Started'}</p>
                </div>
                <div>
                  <p className="text-sm text-white/60 font-normal">Total Amount</p>
                  <p className="font-normal text-white">${fmtAmount(parseFloat(selectedCart.total_amount || 0))}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Passenger Dialog - Dark Theme */}
      <Dialog open={!!selectedPassenger} onOpenChange={() => setSelectedPassenger(null)}>
        <DialogContent className="max-w-lg bg-gradient-to-b from-[#0A1233] to-[#070E28] border-brand-800/50 rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-white font-normal tracking-tight">Edit Passenger</DialogTitle>
            <DialogDescription className="text-white/60 font-normal">Update passenger information</DialogDescription>
          </DialogHeader>
          {selectedPassenger && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm text-white/80 font-normal">First Name</label>
                <Input
                  value={editedPassengerData.givenName}
                  onChange={(e) => setEditedPassengerData({ ...editedPassengerData, givenName: e.target.value })}
                  placeholder="First Name"
                  className={FIELD_BASE}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-white/80 font-normal">Last Name</label>
                <Input
                  value={editedPassengerData.familyName}
                  onChange={(e) => setEditedPassengerData({ ...editedPassengerData, familyName: e.target.value })}
                  placeholder="Last Name"
                  className={FIELD_BASE}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-white/80 font-normal">Date of Birth</label>
                <Input
                  type="date"
                  value={editedPassengerData.bornOn}
                  onChange={(e) => setEditedPassengerData({ ...editedPassengerData, bornOn: e.target.value })}
                  className={FIELD_BASE}
                />
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleSavePassenger}
                  disabled={isSaving}
                  className={cn('flex-1 rounded-full', BRAND_PRIMARY_BTN_CLASS)}
                >
                  {isSaving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Save Changes</>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setSelectedPassenger(null)}
                  className="rounded-full border-white/10 bg-transparent text-white/70 hover:bg-white/[0.06] hover:text-white"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Miles Redemption Dialog - Dark Theme */}
      <Dialog open={showRedemptionModal} onOpenChange={setShowRedemptionModal}>
        <DialogContent className="max-w-md bg-gradient-to-b from-[#0A1233] to-[#070E28] border-brand-800/50 rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-white font-normal tracking-tight">Redeem Defera Miles</DialogTitle>
            <DialogDescription className="text-white/60 font-normal">
              Manually deduct miles from user's account
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-brand-500/10 border border-brand-500/20 rounded-xl p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-brand-200/70 font-normal">Available Balance</span>
                <span className="text-lg font-light text-brand-100">{milesBalance.toLocaleString()} miles</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm text-white/80 font-normal">Amount to Redeem</label>
              <Input
                type="number"
                value={redemptionAmount}
                onChange={(e) => setRedemptionAmount(e.target.value)}
                placeholder="Enter amount"
                min="1"
                max={milesBalance}
                className={FIELD_BASE}
              />
              <p className="text-xs text-white/40 font-normal">Max: {milesBalance.toLocaleString()} miles</p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm text-white/80 font-normal">Reason (minimum 10 characters)</label>
              <textarea
                value={redemptionReason}
                onChange={(e) => setRedemptionReason(e.target.value)}
                placeholder="e.g., Customer service refund for booking #12345"
                rows={3}
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm font-normal focus:outline-none focus:ring-1 focus:ring-[hsl(228,35%,58%)]/30 focus:border-[hsl(228,35%,58%)]/30 placeholder:text-white/35"
              />
              <p className="text-xs text-white/40 font-normal">
                {redemptionReason.length}/10 characters minimum
              </p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleRedeemMiles}
                disabled={isSaving || !redemptionAmount || !redemptionReason || redemptionReason.trim().length < 10}
                className="flex-1 rounded-full bg-rose-500/20 hover:bg-rose-500/30 text-rose-100 border border-rose-500/30"
              >
                {isSaving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redeeming...</>
                ) : (
                  <><Minus className="w-4 h-4 mr-2" /> Redeem Miles</>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowRedemptionModal(false);
                  setRedemptionAmount('');
                  setRedemptionReason('');
                }}
                disabled={isSaving}
                className="rounded-full border-white/10 bg-transparent text-white/70 hover:bg-white/[0.06] hover:text-white"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Inject Miles Dialog - Admin only */}
      <Dialog open={showInjectModal} onOpenChange={setShowInjectModal}>
        <DialogContent className="max-w-md bg-gradient-to-b from-[#0A1233] to-[#070E28] border-brand-800/50 rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-white font-normal tracking-tight">Inject Defera Miles</DialogTitle>
            <DialogDescription className="text-white/60 font-normal">
              Add miles to this customer&apos;s account
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-brand-500/10 border border-brand-500/20 rounded-xl p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-brand-200/70 font-normal">Current Balance</span>
                <span className="text-lg font-light text-brand-100">{milesBalance.toLocaleString()} miles</span>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-white/80 font-normal">Amount to Inject</label>
              <Input
                type="number"
                value={injectAmount}
                onChange={(e) => setInjectAmount(e.target.value)}
                placeholder="Enter amount"
                min="1"
                className={FIELD_BASE}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-white/80 font-normal">Reason (minimum 10 characters)</label>
              <textarea
                value={injectReason}
                onChange={(e) => setInjectReason(e.target.value)}
                placeholder="e.g., Customer service goodwill for delayed flight"
                rows={3}
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm font-normal focus:outline-none focus:ring-1 focus:ring-[hsl(228,35%,58%)]/30 focus:border-[hsl(228,35%,58%)]/30 placeholder:text-white/35"
              />
              <p className="text-xs text-white/40 font-normal">{injectReason.length}/10 characters minimum</p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleInjectMiles}
                disabled={isSaving || !injectAmount || !injectReason || injectReason.trim().length < 10}
                className="flex-1 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-100 border border-emerald-500/30"
              >
                {isSaving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Injecting...</>
                ) : (
                  <><Plus className="w-4 h-4 mr-2" /> Inject Miles</>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowInjectModal(false);
                  setInjectAmount('');
                  setInjectReason('');
                }}
                disabled={isSaving}
                className="rounded-full border-white/10 bg-transparent text-white/70 hover:bg-white/[0.06] hover:text-white"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Defera Credit grant / adjust Dialog - Admin only */}
      <Dialog open={showCreditModal} onOpenChange={setShowCreditModal}>
        <DialogContent className="max-w-md bg-gradient-to-b from-[#0A1233] to-[#070E28] border-brand-800/50 rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-white font-normal tracking-tight">
              {creditMode === 'credit' ? 'Grant Defera Credit' : 'Adjust Defera Credit'}
            </DialogTitle>
            <DialogDescription className="text-white/60 font-normal">
              {creditMode === 'credit'
                ? "Add house credit to this customer's account (auto-applies at checkout)"
                : 'Manual adjustment — use a negative amount to remove credit'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-emerald-200/70 font-normal">Current Balance</span>
                <span className="text-lg font-light text-emerald-100">{formatUsdFromCents(deferaCreditCents)}</span>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-white/80 font-normal">
                Amount (USD){creditMode === 'adjustment' ? ' — negative removes credit' : ''}
              </label>
              <Input
                type="number"
                value={creditAmountInput}
                onChange={(e) => setCreditAmountInput(e.target.value)}
                placeholder="e.g., 250.00"
                step="0.01"
                min={creditMode === 'credit' ? '0' : undefined}
                className={FIELD_BASE}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="credit-internal-reason" className="text-sm text-white/80 font-normal">
                Internal reason (minimum 10 characters)
              </label>
              <textarea
                id="credit-internal-reason"
                value={creditReason}
                onChange={(e) => setCreditReason(e.target.value)}
                placeholder="e.g., Goodwill after the schedule change on ABC123, case 1042"
                rows={3}
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm font-normal focus:outline-none focus:ring-1 focus:ring-[hsl(228,35%,58%)]/30 focus:border-[hsl(228,35%,58%)]/30 placeholder:text-white/35"
              />
              <p className="text-xs text-white/40 font-normal">
                Team only. The customer never sees this. {creditReason.length}/10 characters minimum
              </p>
            </div>
            <div className="space-y-2">
              <label htmlFor="credit-customer-note" className="text-sm text-white/80 font-normal">
                Shown to the customer (optional)
              </label>
              <Input
                id="credit-customer-note"
                value={creditCustomerNote}
                onChange={(e) => setCreditCustomerNote(e.target.value)}
                placeholder="e.g., For the schedule change on your trip ABC123"
                maxLength={CUSTOMER_NOTE_MAX_LENGTH}
                className={FIELD_BASE}
              />
              <p className="text-xs text-white/40 font-normal">
                Appears under this entry in their Defera Credits history. Left blank, they see
                {creditMode === 'credit' ? ' “From the Defera team”.' : ' “Made by the Defera team”.'}
                {' '}{creditCustomerNote.trim().length}/{CUSTOMER_NOTE_MAX_LENGTH}
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleGrantCredit}
                disabled={isSaving || !creditAmountInput || !creditReason || creditReason.trim().length < 10}
                className="flex-1 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-100 border border-emerald-500/30"
              >
                {isSaving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                ) : creditMode === 'credit' ? (
                  <><Plus className="w-4 h-4 mr-2" /> Grant Credit</>
                ) : (
                  <><Edit className="w-4 h-4 mr-2" /> Apply Adjustment</>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreditModal(false);
                  setCreditAmountInput('');
                  setCreditReason('');
                  setCreditCustomerNote('');
                }}
                disabled={isSaving}
                className="rounded-full border-white/10 bg-transparent text-white/70 hover:bg-white/[0.06] hover:text-white"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
